import "../ui/styles/supplies.css";
import { Boosts, Supplies } from "@highmarch/app";
import { boostIconUrl, boostLine, fmtBoostLeft } from "../ui/boosts/boostsShelf";
import { SUPPLY_IDS, SupplyId, isSupplyId } from "@highmarch/domain";
import { suppliesRowHtml, supplyWellHtml, twistMiniHtml, twistRowHtml } from "../ui/supplies/preBattleRow";
import { setSupplyNames } from "../ui/supplies/supplyArt";
import { toast } from "../ui/toast";
import { wellHtml } from "../ui/components";
import type { Feature, FeatureRegistry, HubApi, PreBattleContext } from "./registry";

/** The pre-battle rows sit under the battle card's farm row and above the map panel's buttons. */
const ROW_ORDER = 10;

/**
 * Pre-battle supplies (MP-06) and farm twists (MP-25) in the hub: the battle card's and the campaign map's rows (the
 * four wells to arm, the twist switch, a link to the Shop's Supplies from the battle card only), their taps (delegated
 * on the UI root, redrawn in place), the start of every new battle (`prepareRun`: the armed supplies and the twist go
 * into the run's modifiers, taken from the stock), a Guide topic (Bram's line is scripts/supplies.ts) and debug hooks.
 * The rules are @highmarch/app `Supplies`; the stock is the profile's, saved in the `consumables` section by the catalog.
 * Nothing here appears in a level or on the end screen; the Shop's shelf is ui/supplies/suppliesShelf.ts.
 */
export const suppliesFeature: Feature = {
  id: "supplies",
  register(ctx, registry) {
    const supplies = new Supplies(ctx.catalog);
    setSupplyNames(ctx.balance);
    let hub: HubApi | null = null;

    const boosts = new Boosts(ctx.catalog);
    registry.farmRowExtra({ id: "twist", order: ROW_ORDER, html: (c) => farmTwistHtml(supplies, c, ctx.now()) });
    registry.preBattleRow({ id: "supplies", order: ROW_ORDER, html: (c) => preBattleRowHtml(supplies, c, ctx.now(), c.surface === "card" ? boostWellsHtml(boosts, ctx.now()) : "") });
    registry.hubWidget({ id: "supplies", mount: (h) => { hub = h; return { dispose: () => { if (hub === h) hub = null; } }; } });
    registry.prepareRun = (level, modifiers) => supplies.prepareRun(level, modifiers, ctx.now());
    bindTaps(ctx.ui, supplies, registry, () => hub, ctx.persist);

    registry.guideTopic({
      id: "supplies", group: "castle", label: "Supplies", art: { icon: "flag" }, fromLevel: supplies.data.unlockAfterLevel + 1, steps: [],
      lines: [
        "**Supplies** go into one battle each: tap one on the battle card first. Buy them in the Shop, or win them.",
        "**War Banner**: one more checkpoint retry. **Scout's Map**: peek at your next 3 hands.",
        "**Supply Wagon**: a spare reroll from the start. **Rally Horn**: 3 more troops for waves 1–3.",
      ],
    });

    if (ctx.debug && typeof window !== "undefined") {
      (window as unknown as { __tdSupplies: unknown }).__tdSupplies = {
        supplies,
        /** Captures: hold `n` of every supply (or of one). */
        grant: (n = 2, id?: SupplyId) => { for (const s of id ? [id] : SUPPLY_IDS) ctx.catalog.profile.supplies.add(supplies.data, s, n); ctx.persist(); hub?.changed(); },
        arm: (...ids: SupplyId[]) => { for (const id of ids) if (!ctx.catalog.profile.supplies.isArmed(id)) supplies.toggle(id); ctx.persist(); hub?.changed(); },
        twist: (on = true) => { supplies.setTwist(on); ctx.persist(); hub?.changed(); },
      };
    }
  },
};

/**
 * The rows for one surface: the twist switch (a replay of a cleared level), then the supply wells. On the battle card
 * the twist also comes as a well beside the supplies, which supplies.css shows in its place on a short screen (the
 * card must not grow over the hub's top on a 375 × 667 phone); an empty supplies row hides there too.
 */
/** The battle boosts running now, as wells after the supplies (owner 2026-10-21): the potion, the time left on its cap. */
export function boostWellsHtml(b: Boosts, now: number): string {
  // owner 2026-10-23: every boost shows (they are bought with gems); a running one lit with its time left, the rest dim —
  // a tap on a dim one opens the Shop at the Boosts
  if (!b.shopOpen) return "";
  return b.view(now).map((v) => {
    const on = v.left > 0;
    const say = on ? `${boostLine(v.boost)} · ${fmtBoostLeft(v.left)} left` : `${boostLine(v.boost)} for 1 hour: get it in the Shop`;
    return wellHtml({ art: boostIconUrl(v.boost), size: "sm", tag: "button", cap: on ? fmtBoostLeft(v.left) : undefined, dim: !on, className: `boostwell${on ? " on" : ""}`, label: `Boost: ${say}`, data: `data-say="${say}" data-on="${on}"` });
  }).join("");
}

/** The farm twist's well on the battle card's FARM row: a farm run is the only run it changes. */
export function farmTwistHtml(s: Supplies, c: PreBattleContext, now: number): string {
  if (c.surface !== "card" || c.farmLevel === null) return "";
  const t = s.twist(c.farmLevel, now);
  return t ? twistMiniHtml(t) : "";
}

export function preBattleRowHtml(s: Supplies, c: PreBattleContext, now: number, boostWells = ""): string {
  const card = c.surface === "card";
  // On the battle card the farm twist sits on the FARM row (farmTwistHtml, owner 2026-10-23); the map panel keeps its switch.
  const twist = card || !c.cleared ? null : s.twist(c.level, now);
  const boosts = boostWells ? `<div class="suprow boostrow"><span class="suplbl"><b class="disp">Boosts</b></span><div class="supwells">${boostWells}</div></div>` : "";
  // owner 2026-10-23: every supply always shows (they are bought with gems); one not held is dim, and its tap opens the
  // Shop at the Supplies
  const wells = (s.cardShows ? suppliesRowHtml(s.slots(), false) : "") + boosts;
  const body = (twist ? twistRowHtml(twist, false) : "") + wells;
  if (!body) return "";
  const cls = [wells ? "withrow" : "", !twist && !s.anyHeld ? "onlyempty" : ""].filter(Boolean).join(" ");
  // a dim supply or boost opens the Shop: from the battle card only, never in the quiet after a loss (offersAllowed)
  const shop = card && s.shopLinkAllowed(now) ? ` data-shop="1"` : "";
  return `<div class="prebattle pb-${c.surface}${cls ? ` ${cls}` : ""}"${shop}>${body}</div>`;
}

/** Opens the Shop on its Supplies tab and brings `section` (the supplies or the boosts shelf) into view. */
function openShopAt(h: HubApi | null, registry: FeatureRegistry, section: "supplies" | "boosts"): void {
  if (!h) return;
  registry.openShop(h, "gems");
  let tries = 0;
  const find = (): void => {
    const el = h.parent.querySelector<HTMLElement>(`.ssec.${section}`);
    if (!el) { if (++tries < 20) setTimeout(find, 50); return; }   // the tab draws its shelves over the next frames
    // at once, then again as the art above it loads and the page settles (a smooth scroll ended short of it)
    el.scrollIntoView({ block: "start" });
    for (const ms of [200, 500, 900]) setTimeout(() => el.scrollIntoView({ block: "start" }), ms);
  };
  requestAnimationFrame(find);
}

/** The rows' taps, delegated on the UI root (the hub and the map redraw their markup): arm, twist, the Shop link. */
function bindTaps(root: HTMLElement, s: Supplies, registry: FeatureRegistry, hub: () => HubApi | null, persist: () => void): void {
  if (typeof root.addEventListener !== "function") return;   // a headless registry (tests) has no UI root to listen on
  root.addEventListener("click", (e) => {
    const t = e.target as HTMLElement | null;
    const well = t?.closest?.<HTMLElement>(".prebattle .supwell");
    if (well) {
      const id = well.dataset.sup;
      if (!isSupplyId(id)) return;
      const r = s.toggle(id);
      if (r === "none") {
        const slot = s.slots().find((x) => x.item.id === id)!;
        if (well.closest<HTMLElement>(".prebattle")?.dataset.shop === "1") openShopAt(hub(), registry, "supplies");
        else toast(root, `${slot.item.name}: ${slot.item.blurb.toLowerCase()}`);
        return;
      }
      persist();
      const slot = s.slots().find((x) => x.item.id === id)!;
      well.outerHTML = supplyWellHtml(slot);
      // one line on what it does, whichever way it was tapped (owner 2026-10-23: the wells carry no text)
      toast(root, r === "armed" ? `${slot.item.name} packed: ${slot.item.blurb.toLowerCase()}` : `${slot.item.name} stays home`);
      return;
    }
    const boost = t?.closest?.<HTMLElement>(".prebattle .boostwell");
    if (boost) {
      if (boost.dataset.on !== "true" && boost.closest<HTMLElement>(".prebattle")?.dataset.shop === "1") openShopAt(hub(), registry, "boosts");
      else toast(root, boost.dataset.say ?? "");
      return;
    }
    const twist = t?.closest?.<HTMLElement>(".prebattle .suptwist, .farmrow .suptwist");
    if (twist) {
      const on = !twist.classList.contains("on");
      s.setTwist(on);
      persist();
      for (const b of root.querySelectorAll<HTMLElement>(".prebattle .suptwist, .farmrow .suptwist")) { b.classList.toggle("on", on); b.setAttribute("aria-pressed", String(on)); }
      if (twist.dataset.say) toast(root, on ? `Twist on · ${twist.dataset.say}` : "Twist off: a plain replay");
      return;
    }
    if (t?.closest?.(".prebattle .supshop")) openShopAt(hub(), registry, "supplies");
  });
}
